import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Badge,
  Tooltip,
  alpha,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  InputAdornment,
  CircularProgress,
  Paper,
  Popover,
  Drawer,
  ClickAwayListener,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Inventory as InventoryIcon,
  Description as DescriptionIcon,
  Add as AddIcon,
  AccountCircle,
  Logout,
  Settings,
  Notifications,
  Search,
  CheckCircle,
  Warning,
  Info,
  Error as ErrorIcon,
  Delete,
  ShoppingCart,
  Receipt,
  EventNote,
  FileCopy,
  ExpandMore,
  ViewList,
  ViewKanban,
  CalendarMonth,
  BarChart,
  Timeline,
  DarkModeOutlined,
  HelpOutline,
  Keyboard,
  Person,
  Storage,
  GetApp,
  Close,
  MonetizationOn,
  ReportProblem,
  InsertDriveFile,
  Forum,
  AccessTime,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { customerAPI, productAPI, quotationAPI } from '../../services/api';
import { toast } from 'react-toastify';

// Simplified menu configuration - 3 main menus
const topMenuItems = [
  { 
    text: 'Quotations', 
    path: '/quotations',
    hasDropdown: true,
    dropdownItems: [
      { text: 'All Quotations', path: '/quotations' },
      { text: 'New Quotation', path: '/quotations/new' },
    ]
  },
  { 
    text: 'Products', 
    path: '/products',
    hasDropdown: true,
    dropdownItems: [
      { text: 'All Products', path: '/products' },
      { text: 'New Product', path: '/products/new' },
    ]
  },
  { 
    text: 'Customers', 
    path: '/customers',
    hasDropdown: true,
    dropdownItems: [
      { text: 'All Customers', path: '/customers' },
      { text: 'New Customer', path: '/customers/new' },
    ]
  },
];

// Sub-menu items for different sections
const subMenuConfig = {
  '/quotations': [
    { text: 'Quotations', path: '/quotations' },
    { text: 'New Quotation', path: '/quotations/new' },
  ],
  '/contacts': [
    { text: 'Contacts', path: '/contacts' },
    { text: 'New Contact', path: '/contacts/new' },
  ],
  '/sales-orders': [
    { text: 'Sales Orders', path: '/sales-orders' },
  ],
  '/invoices': [
    { text: 'Invoices', path: '/invoices' },
    { text: 'New Invoice', path: '/invoices/new' },
  ],
  '/products': [
    { text: 'Products', path: '/products' },
    { text: 'New Product', path: '/products/new' },
  ],
  '/customers': [
    { text: 'Customers', path: '/customers' },
    { text: 'New Customer', path: '/customers/new' },
  ],
};

const Layout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [dropdownAnchor, setDropdownAnchor] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  // Search state
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ customers: [], products: [], quotations: [] });
  const [searchLoading, setSearchLoading] = useState(false);

  // Notifications state
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [notifications, setNotifications] = useState([
    { id: 1, type: 'success', title: 'Quotation Accepted', message: 'QT-202605-0002 was accepted by TechStart Solutions', time: '2 hours ago', read: false },
    { id: 2, type: 'warning', title: 'Quotation Expiring', message: 'QT-202605-0001 expires in 3 days', time: '5 hours ago', read: false },
    { id: 3, type: 'info', title: 'New Customer', message: 'Sneha Reddy from Innovate.io was added', time: '1 day ago', read: true },
  ]);

  // Search functionality
  const handleSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults({ customers: [], products: [], quotations: [] });
      return;
    }

    setSearchLoading(true);
    try {
      const [customersRes, productsRes, quotationsRes] = await Promise.all([
        customerAPI.getAll({ search: query, limit: 5 }),
        productAPI.getAll({ search: query, limit: 5 }),
        quotationAPI.getAll({ search: query, limit: 5 }),
      ]);

      setSearchResults({
        customers: customersRes.data.customers || [],
        products: productsRes.data.products || [],
        quotations: quotationsRes.data.quotations || [],
      });
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchQuery) {
        handleSearch(searchQuery);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, handleSearch]);

  // Keyboard shortcut for search (Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === 'Escape' && searchOpen) {
        setSearchOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchOpen]);

  const handleSearchResultClick = (type, item) => {
    setSearchOpen(false);
    setSearchQuery('');
    switch (type) {
      case 'customer':
        navigate(`/customers/${item._id}/edit`);
        break;
      case 'product':
        navigate(`/products/${item._id}/edit`);
        break;
      case 'quotation':
        navigate(`/quotations/${item._id}`);
        break;
      default:
        break;
    }
  };

  // Notification handlers
  const handleNotificationClick = (event) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const markAsRead = (id) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    toast.success('All notifications marked as read');
  };

  const deleteNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success': return <CheckCircle sx={{ color: '#28a745' }} />;
      case 'warning': return <Warning sx={{ color: '#ffc107' }} />;
      case 'error': return <ErrorIcon sx={{ color: '#dc3545' }} />;
      default: return <Info sx={{ color: '#17a2b8' }} />;
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    // Clears local QB token AND signs the user out of Microsoft.
    // The server redirects back to /login after Azure logout completes.
    logout({ azure: true });
  };

  // When QB is embedded in the Sales Dashboard via SSO iframe, the dashboard
  // owns the session lifecycle — hide the Log out option here so the user
  // can't desync the two apps. Detected by being inside an iframe.
  const isEmbedded = (() => {
    try {
      return typeof window !== 'undefined' && window.self !== window.top;
    } catch (_) {
      // Cross-origin parent frame access denied also means we're embedded.
      return true;
    }
  })();

  // Expose for debugging + force a new bundle hash on every rebuild so
  // cached browsers refetch instead of serving the previous bundle.
  if (typeof window !== 'undefined') {
    window.__qbEmbedded = isEmbedded;
    window.__qbBuild = 'v3-2026-06-16';
  }

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
    handleDropdownClose();
  };

  // Dropdown handlers
  const handleDropdownOpen = (event, menuText) => {
    setDropdownAnchor(event.currentTarget);
    setActiveDropdown(menuText);
  };

  const handleDropdownClose = () => {
    setDropdownAnchor(null);
    setActiveDropdown(null);
  };

  // Get current section based on path
  const getCurrentSection = () => {
    const path = location.pathname;
    if (path.includes('/quotations')) return 'Sales';
    if (path.includes('/sales-orders')) return 'Orders';
    if (path.includes('/invoices')) return 'To Invoice';
    if (path.includes('/products')) return 'Products';
    if (path.includes('/customers')) return 'CRM';
    if (path.includes('/templates')) return 'Configuration';
    if (path.includes('/activities')) return 'Activities';
    return 'Dashboard';
  };

  // Mobile drawer content
  const mobileDrawer = (
    <Box sx={{ width: 280, bgcolor: '#875A7B', height: '100%', color: 'white', overflow: 'auto' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <Typography variant="h6" fontWeight={600}>T4L CRM</Typography>
      </Box>
      <List>
        {topMenuItems.map((item) => (
          <React.Fragment key={item.text}>
            <ListItemButton
              onClick={() => !item.hasDropdown ? handleNavigation(item.path) : null}
              sx={{
                '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.1)' },
                '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
              }}
            >
              <ListItemText primary={item.text} />
              {item.hasDropdown && <ExpandMore />}
            </ListItemButton>
            {item.hasDropdown && item.dropdownItems && (
              <List sx={{ pl: 2 }}>
                {item.dropdownItems.filter(d => !d.isHeader).map((dropItem, index) => (
                  <ListItemButton
                    key={index}
                    onClick={() => handleNavigation(dropItem.path)}
                    sx={{
                      py: 0.5,
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
                    }}
                  >
                    <ListItemText 
                      primary={dropItem.text} 
                      primaryTypographyProps={{ fontSize: '14px' }}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </React.Fragment>
        ))}
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 1 }} />
        <ListItemButton onClick={() => handleNavigation('/')}>
          <ListItemText primary="Dashboard" />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f0f0f0' }}>
      {/* Odoo-style Top Navigation Bar */}
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: '#875A7B',
          boxShadow: 'none',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ minHeight: '46px !important', px: 2 }}>
          {/* Mobile menu button */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ display: { md: 'none' }, mr: 1 }}
          >
            <MenuIcon />
          </IconButton>

          {/* Back button - hidden on dashboard/root */}
          {location.pathname !== '/' && location.pathname !== '/dashboard' && (
            <IconButton
              color="inherit"
              aria-label="go back"
              onClick={() => {
                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  navigate('/');
                }
              }}
              sx={{ mr: 1 }}
              size="small"
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}

          {/* Forward button - hidden on dashboard/root */}
          {location.pathname !== '/' && location.pathname !== '/dashboard' && (
            <IconButton
              color="inherit"
              aria-label="go forward"
              onClick={() => navigate(1)}
              sx={{ mr: 1 }}
              size="small"
            >
              <ArrowForwardIcon fontSize="small" />
            </IconButton>
          )}

          {/* Logo */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mr: 3,
              cursor: 'pointer',
            }}
            onClick={() => navigate('/')}
          >
            <img
              src="/nav-logo-icon.png"
              alt="T4L CRM Logo"
              style={{
                width: 32,
                height: 32,
                objectFit: 'contain',
              }}
            />
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: 'white',
                display: { xs: 'none', sm: 'block' },
                fontSize: '15px',
              }}
            >
              Quotation Builder
            </Typography>
          </Box>

          {/* Main Navigation Items - Desktop */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
            {topMenuItems.map((item) => (
              <Box
                key={item.text}
                sx={{ position: 'relative' }}
              >
                <Typography
                  variant="body2"
                  onClick={(e) => {
                    if (item.hasDropdown) {
                      if (activeDropdown === item.text) {
                        handleDropdownClose();
                      } else {
                        handleDropdownOpen(e, item.text);
                      }
                    } else {
                      navigate(item.path);
                    }
                  }}
                  sx={{
                    px: 2,
                    py: 1,
                    cursor: 'pointer',
                    color: activeDropdown === item.text ? 'white' : 'rgba(255,255,255,0.9)',
                    backgroundColor: activeDropdown === item.text ? 'rgba(0,0,0,0.15)' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.15)',
                    },
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  {item.text}
                  {item.hasDropdown && <ExpandMore sx={{ fontSize: 16 }} />}
                </Typography>
                
                {/* Dropdown Menu */}
                {item.hasDropdown && activeDropdown === item.text && (
                  <ClickAwayListener onClickAway={handleDropdownClose}>
                    <Paper
                      sx={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        minWidth: 180,
                        zIndex: 1300,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        borderRadius: '4px',
                        py: 0.5,
                        mt: 0.5,
                      }}
                    >
                      {item.dropdownItems.map((dropItem, index) => (
                        dropItem.isHeader ? (
                          <Typography
                            key={index}
                            sx={{
                              px: 2,
                              py: 0.75,
                              fontSize: '12px',
                              fontWeight: 600,
                              color: '#666',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              mt: index > 0 ? 1 : 0,
                            }}
                          >
                            {dropItem.text}
                          </Typography>
                        ) : (
                          <MenuItem
                            key={index}
                            onClick={() => handleNavigation(dropItem.path)}
                            sx={{
                              fontSize: '14px',
                              py: 0.75,
                              px: dropItem.indent ? 3 : 2,
                              color: '#212529',
                              '&:hover': {
                                backgroundColor: '#f5f5f5',
                              },
                            }}
                          >
                            {dropItem.text}
                          </MenuItem>
                        )
                      ))}
                    </Paper>
                  </ClickAwayListener>
                )}
              </Box>
            ))}
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* Right side - User */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* User Avatar */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Avatar
                sx={{
                  width: 30,
                  height: 30,
                  bgcolor: '#E91E63',
                  fontSize: '13px',
                  fontWeight: 600,
                }}
              >
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </Avatar>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
          },
        }}
      >
        {mobileDrawer}
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: '46px',
          backgroundColor: '#f0f0f0',
          minHeight: 'calc(100vh - 46px)',
          overflowX: 'hidden',
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: 0,
            height: 0,
            display: 'none',
          },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        {children}
      </Box>

      {/* Search Dialog */}
      <Dialog
        open={searchOpen}
        onClose={() => { setSearchOpen(false); setSearchQuery(''); }}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '4px',
            bgcolor: 'background.paper',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={600}>Search</Typography>
          <IconButton onClick={() => { setSearchOpen(false); setSearchQuery(''); }} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            autoFocus
            placeholder="Search customers, products, quotations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {searchLoading ? <CircularProgress size={20} /> : <Search />}
                </InputAdornment>
              ),
            }}
          />

          {/* Search Results */}
          {searchQuery.length >= 2 && (
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {/* Customers */}
              {searchResults.customers.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, px: 1 }}>
                    CUSTOMERS
                  </Typography>
                  {searchResults.customers.map((customer) => (
                    <Paper
                      key={customer._id}
                      onClick={() => handleSearchResultClick('customer', customer)}
                      sx={{
                        p: 1.5,
                        mb: 1,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.08)' },
                      }}
                    >
                      <Avatar sx={{ width: 32, height: 32, bgcolor: '#ec4899' }}>
                        <PeopleIcon sx={{ fontSize: 18 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{customer.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{customer.company}</Typography>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}

              {/* Products */}
              {searchResults.products.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, px: 1 }}>
                    PRODUCTS
                  </Typography>
                  {searchResults.products.map((product) => (
                    <Paper
                      key={product._id}
                      onClick={() => handleSearchResultClick('product', product)}
                      sx={{
                        p: 1.5,
                        mb: 1,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.08)' },
                      }}
                    >
                      <Avatar sx={{ width: 32, height: 32, bgcolor: '#f59e0b' }}>
                        <InventoryIcon sx={{ fontSize: 18 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{product.name}</Typography>
                        <Typography variant="caption" color="text.secondary">₹{product.price.toLocaleString()}</Typography>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}

              {/* Quotations */}
              {searchResults.quotations.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, px: 1 }}>
                    QUOTATIONS
                  </Typography>
                  {searchResults.quotations.map((quotation) => (
                    <Paper
                      key={quotation._id}
                      onClick={() => handleSearchResultClick('quotation', quotation)}
                      sx={{
                        p: 1.5,
                        mb: 1,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.08)' },
                      }}
                    >
                      <Avatar sx={{ width: 32, height: 32, bgcolor: '#8b5cf6' }}>
                        <DescriptionIcon sx={{ fontSize: 18 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{quotation.quotationNumber}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {quotation.customer?.name} - ₹{quotation.total?.toLocaleString()}
                        </Typography>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}

              {/* No Results */}
              {!searchLoading && 
               searchResults.customers.length === 0 && 
               searchResults.products.length === 0 && 
               searchResults.quotations.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">No results found for "{searchQuery}"</Typography>
                </Box>
              )}
            </Box>
          )}

          {searchQuery.length > 0 && searchQuery.length < 2 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              Type at least 2 characters to search...
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Notifications Popover */}
      <Popover
        open={Boolean(notificationAnchor)}
        anchorEl={notificationAnchor}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: 360,
            maxHeight: 480,
            borderRadius: 3,
            mt: 1,
          }
        }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
          <Typography variant="h6" fontWeight={600}>Notifications</Typography>
          {unreadCount > 0 && (
            <Chip
              label="Mark all read"
              size="small"
              onClick={markAllAsRead}
              sx={{ cursor: 'pointer', fontSize: '0.7rem' }}
            />
          )}
        </Box>
        
        {notifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Notifications sx={{ fontSize: 48, color: 'rgba(0,0,0,0.2)', mb: 1 }} />
            <Typography color="text.secondary">No notifications</Typography>
          </Box>
        ) : (
          <List sx={{ py: 0 }}>
            {notifications.map((notification) => (
              <ListItem
                key={notification.id}
                sx={{
                  bgcolor: notification.read ? 'transparent' : 'rgba(99, 102, 241, 0.04)',
                  '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.08)' },
                  borderBottom: '1px solid rgba(0,0,0,0.04)',
                }}
                secondaryAction={
                  <IconButton size="small" onClick={() => deleteNotification(notification.id)}>
                    <Delete sx={{ fontSize: 16, color: 'text.secondary' }} />
                  </IconButton>
                }
              >
                <ListItemButton onClick={() => markAsRead(notification.id)} sx={{ py: 1.5 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {getNotificationIcon(notification.type)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={notification.read ? 400 : 600}>
                        {notification.title}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                          {notification.message}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
                          {notification.time}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Popover>
    </Box>
  );
};

export default Layout;
